import { useEffect, useCallback } from 'react';
import { tokenSyncService } from '@/services/tokenSync';
import { TokenStorage } from '@/config/storage';
import { api } from '@/services/api';

interface UseTokenSyncOptions {
  // Se deve sincronizar tokens automaticamente
  enableSync?: boolean;
  // Callback chamado quando o token é sincronizado de outra aba
  onTokenSynced?: (accessToken: string, refreshToken: string) => void;
  // Callback chamado quando a sessão é limpa em outra aba
  onSessionCleared?: () => void;
  // Callback chamado quando o token expira em outra aba
  onTokenExpired?: () => void;
}

export function useTokenSync(options: UseTokenSyncOptions = {}) {
  const {
    enableSync = true,
    onTokenSynced,
    onSessionCleared,
    onTokenExpired,
  } = options;

  // Callback para quando o token é sincronizado
  const handleTokenSynced = useCallback((data: { accessToken: string; refreshToken: string }) => {
    console.log('[useTokenSync] Token sincronizado de outra aba');
    
    // Atualiza o token no axios
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
    
    if (onTokenSynced) {
      onTokenSynced(data.accessToken, data.refreshToken);
    }
  }, [onTokenSynced]);

  // Callback para quando a sessão é limpa
  const handleSessionCleared = useCallback(() => {
    console.log('[useTokenSync] Sessão limpa em outra aba - mantendo conexão local');
    
    // Não remove o token do axios para manter a conexão
    // A sessão será mantida até que o usuário faça logout manualmente
    
    if (onSessionCleared) {
      onSessionCleared();
    }
  }, [onSessionCleared]);

  // Callback para quando o token expira
  const handleTokenExpired = useCallback(() => {
    console.log('[useTokenSync] Token expirado em outra aba');
    
    if (onTokenExpired) {
      onTokenExpired();
    }
  }, [onTokenExpired]);

  // Effect para configurar os listeners de sincronização
  useEffect(() => {
    if (!enableSync) {
      return;
    }

    // Adiciona listeners para eventos de sincronização
    tokenSyncService.addListener('multimarcas:token-refreshed', handleTokenSynced);
    tokenSyncService.addListener('multimarcas:session-cleared', handleSessionCleared);
    tokenSyncService.addListener('multimarcas:token-expired', handleTokenExpired);

    return () => {
      // Remove os listeners quando o componente é desmontado
      tokenSyncService.removeListener('multimarcas:token-refreshed', handleTokenSynced);
      tokenSyncService.removeListener('multimarcas:session-cleared', handleSessionCleared);
      tokenSyncService.removeListener('multimarcas:token-expired', handleTokenExpired);
    };
  }, [enableSync, handleTokenSynced, handleSessionCleared, handleTokenExpired]);

  // Função para notificar outras abas sobre renovação de token
  const notifyTokenRefreshed = useCallback((accessToken: string, refreshToken: string) => {
    tokenSyncService.notifyTokenRefreshed(accessToken, refreshToken);
  }, []);

  // Função para notificar outras abas sobre limpeza de sessão
  const notifySessionCleared = useCallback(() => {
    tokenSyncService.notifySessionCleared();
  }, []);

  // Função para notificar outras abas sobre expiração de token
  const notifyTokenExpired = useCallback(() => {
    tokenSyncService.notifyTokenExpired();
  }, []);

  return {
    notifyTokenRefreshed,
    notifySessionCleared,
    notifyTokenExpired,
  };
} 